import { useCallback } from 'react';
import { Info } from 'lucide-react';
import posthog from 'posthog-js';
import debounce from 'lodash.debounce';

import ComboBox from './ComboBox';

import { useStore } from '../hooks/useStore';
import useTranslation from '../hooks/useTranslation';

import { FoxwayModel } from '@/lib/types/foxway';
import { DeviceType } from '@/lib/constants/foxway';
import searchLabel from '@/lib/utils/searchLabel';
import HowtoLink from './HowtoLink';
import imeikeypad from '@/public/assets/imeikeypad.png';

const getOptionLabel = (model: FoxwayModel) => model.ProductName ?? '';

const filterOption = (model: FoxwayModel, query: string) =>
  searchLabel(model.ProductName ?? '', query);

const formatOptionLabel = (model: FoxwayModel) => <OptionLabel model={model} />;

const OptionLabel = ({ model }: { model: FoxwayModel }) => (
  <div className="flex grow items-center text-base">
    <div className="flex grow">{model.ProductName}</div>
  </div>
);

const ModelSelect = () => {
  const models = useStore((state) => state.models);
  const loadingModels = useStore((state) => state.loadingModels);
  const modelsQuery = useStore((state) => state.modelsQuery);
  const setModelsQuery = useStore((state) => state.setModelsQuery);
  const getModels = useStore((state) => state.getModels);
  const selectedDeviceType = useStore((state) => state.selectedDeviceType);
  const selectedModel = useStore((state) => state.selectedModel);
  const setSelectedModel = useStore((state) => state.setSelectedModel);

  const translations = useTranslation();

  const onInputChange = useCallback(
    debounce((query: string) => {
      setModelsQuery(query);
      getModels();
    }, 300),
    [setModelsQuery, getModels],
  );

  const onChange = useCallback(
    (model: FoxwayModel | null) => {
      if (!selectedModel || selectedModel?.Id !== model?.Id) {
        setSelectedModel(model);
      }
      if (model?.ProductName) {
        posthog.capture('Select Model', { model: model.ProductName });
      }
    },

    [selectedModel, setSelectedModel],
  );

  return (
    <section className="relative z-10">
      <ComboBox
        formatOptionLabel={formatOptionLabel}
        getOptionLabel={getOptionLabel}
        filterOption={filterOption}
        isLoading={loadingModels}
        onChange={onChange}
        onInputChange={onInputChange}
        options={models}
        label={
          selectedDeviceType === DeviceType.mobile
            ? translations.modelMobileTitle
            : translations.modelTitle
        }
        placeholder={
          selectedDeviceType === DeviceType.mobile
            ? translations.modelMobilePlaceholder
            : translations.modelPlaceholder
        }
        description={
          <>
            {selectedDeviceType === DeviceType.mobile && (
              <div className="flex flex-col sm:flex-row gap-4 my-2 items-center">
                <img src={imeikeypad} alt="" className="w-26 h-auto border" />
                <div className="flex flex-col gap-2 text-base">
                  <div className="flex gap-2">
                    <Info size={25} />
                    <p className="font-samsungOneBold">
                      {translations.modelImeiTitel}
                    </p>
                  </div>
                  <ol className="grid gap-2 list-none text-left text-base">
                    <li>
                      {translations.modelImeiStep1Prefix}
                      <span className="font-samsungOneBold bg-gray-100 px-2 py-0.5 rounded">
                        *#06#
                      </span>
                      {translations.modelImeiStep1Suffix}
                    </li>
                    <li>{translations.modelImeiStep2}</li>
                  </ol>
                </div>
              </div>
            )}
            <HowtoLink />
          </>
        }
        value={selectedModel}
        error={
          modelsQuery.length > 3 && !models.length && !loadingModels
            ? translations['modelError.deviceNotFound']
            : undefined
        }
      />
    </section>
  );
};

export default ModelSelect;
